import { Grid, Link as MuiLink, Stack } from '@mui/material';
import { debounce, isString, set } from 'lodash';
import { Path } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { NumberInputWidget } from 'src/components/widget/numberInputWidget/NumberInputWidget';
import { SwitchButtonWidget } from 'src/components/widget/switchButtonWidget/SwitchButtonWidget';
import { PATHS } from 'src/constants/paths';
import { ProductMoleculeItem } from 'src/features/components/editComponentPage/forms/modules/productMoleculeItem/ProductMoleculeItem';
import { type IGcfProductListProductConfig } from 'src/interfaces';
import { lang } from 'src/utils';

import { CustomStyledAlert } from '../../EditProductPanel.styled';
import { ProductMoleculeStylingProps } from './ProductMoleculeStyling.interface';

export const ProductMoleculeStyling = (props: ProductMoleculeStylingProps) => {
  const categorySizeName = Object.keys(props.theme?.config.category ?? {})[0];

  const debounceHandler = debounce((field: string, value: any) => {
    props.onChange(
      set({ ...props.value } as IGcfProductListProductConfig, field, value),
    );
  }, 500);

  const handleFieldChange =
    (
      field: Path<ProductMoleculeStylingProps['value']>,
      enableDebounce?: boolean,
    ) =>
    (value: any, fieldDetails?: any) => {
      if (field === 'hideDisplayName') {
        value = !value;
      }

      if (enableDebounce) {
        debounceHandler(
          isString(fieldDetails) ? `${field}.${fieldDetails}` : field,
          value,
        );
      } else {
        props.onChange(
          set(
            { ...props.value } as IGcfProductListProductConfig,
            isString(fieldDetails) ? `${field}.${fieldDetails}` : field,
            value,
          ),
        );
      }
    };

  return (
    <Stack sx={{ gap: '20px' }}>
      {props.options.showProductPositionsController && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <SwitchButtonWidget
              label={lang.displayProductName}
              value={!props.value?.hideDisplayName}
              onChange={handleFieldChange('hideDisplayName')}
            />
          </Grid>

          <Grid item xs={6}>
            <NumberInputWidget
              label={lang.xAxis}
              value={props?.value?.positionLeft}
              onChange={handleFieldChange('positionLeft')}
            />
          </Grid>

          <Grid item xs={6}>
            <NumberInputWidget
              label={lang.yAxis}
              value={props?.value?.positionTop}
              onChange={handleFieldChange('positionTop')}
            />
          </Grid>
        </Grid>
      )}

      <ProductMoleculeItem
        title={lang.sizeLabel}
        value={{
          ...props?.theme?.config?.subproducts?.label,
          ...props?.componentSizesStyles?.defaultConfig?.styles?.label,
          ...props.value?.sizes?.defaultConfig?.styles?.label,
        }}
        handleChange={handleFieldChange('sizes.defaultConfig.styles.label')}
        options={{ showBgColor: true, showHeight: true, showDivider: true }}
      />

      <ProductMoleculeItem
        title={lang.productFlavor}
        value={{
          ...props.theme?.config.subproducts?.flavor,
          ...props.componentSizesStyles?.defaultConfig?.styles?.flavor,
          ...props.value?.sizes?.defaultConfig?.styles?.flavor,
        }}
        handleChange={handleFieldChange('sizes.defaultConfig.styles.flavor')}
        options={{
          showDivider: true,
          showBrandedColorPicker: ['color'],
          colorPickerPosition: 'right',
        }}
      />

      <ProductMoleculeItem
        title={lang.productPrice}
        value={{
          ...props.theme?.config.subproducts?.price,
          ...props.componentSizesStyles?.defaultConfig?.styles?.price,
          ...props.value?.sizes?.defaultConfig?.styles?.price,
        }}
        handleChange={handleFieldChange('sizes.defaultConfig.styles.price')}
        options={{
          showDivider: true,
          showBrandedColorPicker: ['color'],
          colorPickerPosition: 'right',
        }}
      />

      <ProductMoleculeItem
        title={lang.productNutrition}
        value={{
          ...props.theme?.config.subproducts?.nutrition,
          ...props.componentSizesStyles?.defaultConfig?.styles?.nutrition,
          ...props.value?.sizes?.defaultConfig?.styles?.nutrition,
        }}
        handleChange={handleFieldChange('sizes.defaultConfig.styles.nutrition')}
        options={{
          showDivider: true,
          showFlexWrap: props.options?.showFlexWrap,
          showBrandedColorPicker: ['color'],
          colorPickerPosition: 'right',
        }}
      />

      <ProductMoleculeItem
        title={lang.productDisclaimer}
        value={{
          ...props.theme?.config.subproducts?.disclaimer,
          ...props.componentSizesStyles?.defaultConfig?.styles?.disclaimer,
          ...props.value?.sizes?.defaultConfig?.styles?.disclaimer,
        }}
        handleChange={handleFieldChange(
          'sizes.defaultConfig.styles.disclaimer',
        )}
        options={{
          showDivider: true,
          hideAllElements: !props.value?.displayProductDisclaimer,
          showBrandedColorPicker: ['color'],
          colorPickerPosition: 'top',
        }}
        customElement={
          <Grid item xs={12}>
            <CustomStyledAlert icon={false}>
              {lang.productDisclaimerAlertText}
              {props.options.enableNavigateToSetting ? (
                <MuiLink
                  component={Link}
                  to={PATHS.SETTINGS_CONFIGURATIONS}
                  target="_blank">
                  {lang.settings}
                </MuiLink>
              ) : (
                lang.settings
              )}
            </CustomStyledAlert>
            <SwitchButtonWidget
              value={props?.value?.displayProductDisclaimer}
              onChange={handleFieldChange('displayProductDisclaimer')}
            />
          </Grid>
        }
      />

      {props.options.showSizeStylesController && (
        <>
          <ProductMoleculeItem
            title={lang.alacarteLabel}
            value={{
              ...props.theme?.config.category?.[categorySizeName]?.label,
              ...props.componentSizesStyles?.[categorySizeName]?.styles?.label,
              ...props.value?.sizes?.[categorySizeName]?.styles?.label,
            }}
            handleChange={handleFieldChange(
              `sizes.${categorySizeName}.styles.label`,
            )}
            options={{ showDivider: true }}
          />

          <ProductMoleculeItem
            title={lang.alacartePrice}
            value={{
              ...props.theme?.config.category?.[categorySizeName]?.price,
              ...props.componentSizesStyles?.[categorySizeName]?.styles?.price,
              ...props.value?.sizes?.[categorySizeName]?.styles?.price,
            }}
            handleChange={handleFieldChange(
              `sizes.${categorySizeName}.styles.price`,
            )}
            options={{
              showDivider: true,
              showBrandedColorPicker: ['color'],
              colorPickerPosition: 'right',
            }}
          />

          <ProductMoleculeItem
            title={lang.alacarteNutrition}
            value={{
              ...props.theme?.config.category?.[categorySizeName]?.nutrition,
              ...props.componentSizesStyles?.[categorySizeName]?.styles
                ?.nutrition,
              ...props.value?.sizes?.[categorySizeName]?.styles?.nutrition,
            }}
            options={{
              showBrandedColorPicker: ['color'],
              colorPickerPosition: 'top',
            }}
            handleChange={handleFieldChange(
              `sizes.${categorySizeName}.styles.nutrition`,
            )}
          />
        </>
      )}
    </Stack>
  );
};
